import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import Button from "@mui/material/Button";
import IconButton from "@mui/material/IconButton";
import CircularProgress from "@mui/material/CircularProgress";
import CloseIcon from "@mui/icons-material/Close";
import { EvaluationVideoMediaCard } from "./EvaluationVideoMediaCard";
import { ReviewVideoFullscreenPlayer } from "./ReviewVideoFullscreenPlayer";
import { MoveEvaluationVideoDialog } from "./MoveEvaluationVideoDialog";
import ClipShareSheet from "./social/ClipShareSheet";
import { fetchSessionEvaluationVideos } from "../api/sessionMediaApi";
import { sessionlist } from "./funtionsBD/Mainservice";
import { prefetchSignedMediaUrls } from "../api/signedMediaUrl";
import { formatTrainingSessionLabel } from "../utils/formatTrainingSessionLabel";
import {
  extractSessionListRows,
  sessionCalendarDayKey,
  sessionsEligibleForVideoImport,
} from "../utils/sessionMediaImport";
import {
  reencodeEvaluationVideo,
  formatReencodeEta,
} from "../api/evaluationVideoBakeApi";
import { fetchOrEnqueuePoseResult } from "../api/poseAnalysisApi";
import { getActiveTableGeometry } from "../api/tableGeometryApi";
import {
  loadVideoAnalysisSettings,
  roisForPoseDetectionPlayer,
} from "../utils/videoAnalysisSettings";
import { hasOrientVideoTransform } from "../utils/videoViewSetup";
import { toAbsoluteMediaUrl } from "../utils/kotPlayerMatches";
import {
  buildEvaluationVideoMetadata,
  formatEvaluationVideoCardDate,
  resolveExerciseDisplayName,
  isMaxRallyEvaluationVideo,
  isStrokeSlowMoEvaluationVideo,
} from "../utils/evaluationVideoMediaMeta";
import { ip_back } from "../config";
import "./PlayerSessionResultsDialog.css";
import "./SessionMediaFilesDialog.css";

const FILTERS = Object.freeze(["all", "evaluation", "kot", "table_replay"]);

function clipToMsg(clip) {
  const kind = clip.kind || (clip.is_kot ? "kot" : "evaluation");
  return {
    message_id: clip.message_id,
    te_id: clip.te_id,
    table_id: clip.table_id,
    sender_id: clip.sender_id || null,
    message_type: kind === "table_replay" ? "video" : "evaluation",
    video_message_url: clip.video_url || clip.video_message_url,
    text_message: clip.text_message,
    view_setup: clip.view_setup,
    sequence_number: clip.sequence_number,
    createdAt: clip.createdAt,
    is_kot: clip.is_kot,
    kind,
    kot_match_id: clip.kot_match_id,
    sender_player_hand: clip.sender_player_hand || null,
  };
}

function buildSessionClipMeta(clip, exercises, localeCode, t) {
  const exercise =
    (exercises || []).find((e) => String(e?.te_id) === String(clip.te_id)) ||
    null;
  const msg = clipToMsg(clip);
  const sequences = Array.isArray(exercise?.sequences)
    ? exercise.sequences
    : [];
  const kotMatches =
    exercise?.kot_matches ||
    exercise?.matches ||
    exercise?.kotMatches ||
    null;

  return buildEvaluationVideoMetadata({
    msg,
    exercise: exercise || {
      te_id: clip.te_id,
      te_order: clip.te_order,
    },
    fullExercise: exercise,
    exName: resolveExerciseDisplayName(exercise, t),
    sequences,
    viewerIdentityIds: [],
    localeCode,
    t,
    kotMatches: Array.isArray(kotMatches) ? kotMatches : null,
  });
}

/**
 * Session-level media browser: evaluation + KoT videos.
 * Reuses Media-tab EvaluationVideoMediaCard look & feel.
 */
export function SessionMediaFilesDialog({
  open,
  onClose,
  tsId,
  grId = null,
  sessionDate = null,
  exercises = [],
  localeCode = "en",
  t = (k, d) => d || k,
  onPlayClip,
  onPoseComplete,
}) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [clips, setClips] = useState([]);
  const [counts, setCounts] = useState({
    total: 0,
    evaluation: 0,
    kot: 0,
    table_replay: 0,
  });
  const [filter, setFilter] = useState("all");
  const [jobs, setJobs] = useState({});
  const [bakingMessageId, setBakingMessageId] = useState(null);
  const [detectingMessageId, setDetectingMessageId] = useState(null);
  const [batchDetectBusy, setBatchDetectBusy] = useState(false);
  const [moveVideoTarget, setMoveVideoTarget] = useState(null);
  const [reelShareTarget, setReelShareTarget] = useState(null);
  const [playback, setPlayback] = useState(null);
  const [lastPlayedId, setLastPlayedId] = useState(null);
  const [returnPulseId, setReturnPulseId] = useState(null);
  const [moveStatus, setMoveStatus] = useState(null);
  const [importSessions, setImportSessions] = useState([]);
  const [importSessionsLoading, setImportSessionsLoading] = useState(false);
  const [sourceTsId, setSourceTsId] = useState("");
  const [sourceClips, setSourceClips] = useState([]);
  const [sourceCounts, setSourceCounts] = useState({
    total: 0,
    evaluation: 0,
    kot: 0,
    table_replay: 0,
  });
  const [sourceLoading, setSourceLoading] = useState(false);
  const [sourceError, setSourceError] = useState(null);
  const abortByIdRef = useRef(new Map());
  const geoCacheRef = useRef(new Map());
  const cardsRef = useRef(null);
  const returnPulseTimerRef = useRef(null);

  const viewingImport = Boolean(sourceTsId);
  const displayedClips = viewingImport ? sourceClips : clips;
  const displayedCounts = viewingImport ? sourceCounts : counts;

  useEffect(() => {
    if (!open || !displayedClips.length) return;
    prefetchSignedMediaUrls(
      displayedClips
        .map((clip) => clip?.video_url || clip?.video_message_url)
        .filter(Boolean)
    );
  }, [open, displayedClips]);

  const reloadClips = useCallback(async () => {
    if (!tsId) return;
    const data = await fetchSessionEvaluationVideos(tsId);
    setClips(Array.isArray(data?.clips) ? data.clips : []);
    setCounts(
      data?.counts || {
        total: data?.clips?.length || 0,
        evaluation: 0,
        kot: 0,
        table_replay: 0,
      }
    );
  }, [tsId]);

  const reloadSourceClips = useCallback(async (importTsId) => {
    if (!importTsId) {
      setSourceClips([]);
      setSourceCounts({ total: 0, evaluation: 0, kot: 0, table_replay: 0 });
      setSourceError(null);
      return;
    }
    const data = await fetchSessionEvaluationVideos(importTsId);
    setSourceClips(Array.isArray(data?.clips) ? data.clips : []);
    setSourceCounts(
      data?.counts || {
        total: data?.clips?.length || 0,
        evaluation: 0,
        kot: 0,
        table_replay: 0,
      }
    );
  }, []);

  useEffect(() => {
    if (!open || !tsId) return;
    let cancelled = false;
    async function load() {
      setLoading(true);
      setError(null);
      try {
        await reloadClips();
      } catch (err) {
        if (!cancelled) {
          setClips([]);
          setError(
            err?.message ||
              t("sessionMediaLoadFailed", "Could not load session media files")
          );
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    load();
    return () => {
      cancelled = true;
    };
  }, [open, tsId, t, reloadClips]);

  useEffect(() => {
    if (!open) {
      setFilter("all");
      setSourceTsId("");
      setSourceClips([]);
      setSourceError(null);
      setImportSessions([]);
    }
  }, [open]);

  useEffect(() => {
    if (!open || !tsId) return;
    let cancelled = false;
    async function loadImportSessions() {
      setImportSessionsLoading(true);
      try {
        const day = sessionCalendarDayKey(sessionDate);
        const params = {};
        if (day) params.selected_date = day;
        if (grId) params.gr_id = grId;
        let rows = [];
        if (day || grId) {
          const response = await sessionlist(params);
          rows = extractSessionListRows(response);
        }
        if ((!rows || rows.length === 0) && day) {
          const fallback = await sessionlist({ selected_date: day });
          rows = extractSessionListRows(fallback);
        }
        if (!cancelled) {
          setImportSessions(sessionsEligibleForVideoImport(rows, tsId));
        }
      } catch {
        if (!cancelled) setImportSessions([]);
      } finally {
        if (!cancelled) setImportSessionsLoading(false);
      }
    }
    loadImportSessions();
    return () => {
      cancelled = true;
    };
  }, [open, tsId, grId, sessionDate]);

  useEffect(() => {
    if (!open || !sourceTsId) {
      setSourceClips([]);
      setSourceError(null);
      setSourceLoading(false);
      return undefined;
    }
    let cancelled = false;
    async function loadSource() {
      setSourceLoading(true);
      setSourceError(null);
      try {
        await reloadSourceClips(sourceTsId);
      } catch (err) {
        if (!cancelled) {
          setSourceClips([]);
          setSourceError(
            err?.message ||
              t("sessionMediaLoadFailed", "Could not load session media files")
          );
        }
      } finally {
        if (!cancelled) setSourceLoading(false);
      }
    }
    loadSource();
    return () => {
      cancelled = true;
    };
  }, [open, sourceTsId, reloadSourceClips, t]);

  const handleMoveVideoSuccess = useCallback(
    async (updated) => {
      setMoveVideoTarget(null);
      try {
        await reloadClips();
        if (sourceTsId) await reloadSourceClips(sourceTsId);
      } catch {
        /* keep list; status still useful */
      }
      const stillHere =
        updated?.message_id &&
        String(updated.ts_id || tsId) === String(tsId);
      setMoveStatus({
        success: true,
        message: stillHere
          ? t("moveVideoSuccess", "Video moved successfully.")
          : t(
              "moveVideoSuccessAway",
              "Video moved to another exercise. Refresh if it still appears here."
            ),
      });
      window.setTimeout(() => setMoveStatus(null), 5000);
    },
    [reloadClips, reloadSourceClips, sourceTsId, t, tsId]
  );

  const handleDeleteVideoSuccess = useCallback(async () => {
    setMoveVideoTarget(null);
    try {
      await reloadClips();
      if (sourceTsId) await reloadSourceClips(sourceTsId);
    } catch {
      /* ignore */
    }
    setMoveStatus({
      success: true,
      message: t("deleteEvaluationVideoSuccess", "Evaluation video deleted."),
    });
    window.setTimeout(() => setMoveStatus(null), 5000);
  }, [reloadClips, reloadSourceClips, sourceTsId, t]);

  useEffect(() => {
    return () => {
      for (const ac of abortByIdRef.current.values()) {
        try {
          ac.abort();
        } catch {
          /* ignore */
        }
      }
      abortByIdRef.current.clear();
    };
  }, []);

  const updateJob = useCallback((id, patch) => {
    setJobs((prev) => ({
      ...prev,
      [id]: { ...(prev[id] || {}), ...patch },
    }));
  }, []);

  const clearJobSoon = useCallback((id, delayMs = 2500) => {
    window.setTimeout(() => {
      setJobs((prev) => {
        if (!prev[id] || prev[id].status === "running") return prev;
        const next = { ...prev };
        delete next[id];
        return next;
      });
    }, delayMs);
  }, []);

  const filtered = useMemo(() => {
    if (filter === "evaluation") {
      return displayedClips.filter(
        (c) => c.kind === "evaluation" || (!c.kind && !c.is_kot)
      );
    }
    if (filter === "kot") {
      return displayedClips.filter((c) => c.kind === "kot" || c.is_kot);
    }
    if (filter === "table_replay") {
      return displayedClips.filter((c) => c.kind === "table_replay");
    }
    return displayedClips;
  }, [displayedClips, filter]);

  const runningJobs = useMemo(
    () => Object.entries(jobs).filter(([, j]) => j?.status === "running"),
    [jobs]
  );

  const longestEta = useMemo(() => {
    let max = null;
    for (const [, j] of runningJobs) {
      const eta = Number(j?.etaSeconds);
      if (!Number.isFinite(eta) || eta <= 0) continue;
      if (max == null || eta > max) max = eta;
    }
    return max;
  }, [runningJobs]);

  const filterLabels = {
    all: t("sessionMediaFilterAll", "All"),
    evaluation: t("sessionMediaFilterEval", "Evaluation"),
    kot: t("sessionMediaFilterKot", "KoT"),
    table_replay: t("sessionMediaFilterReplay", "Replay"),
  };

  const filterCounts = {
    all: displayedCounts.total ?? displayedClips.length,
    evaluation: displayedCounts.evaluation ?? 0,
    kot: displayedCounts.kot ?? 0,
    table_replay: displayedCounts.table_replay ?? 0,
  };

  const sourceSessionLabel = useMemo(() => {
    const row = importSessions.find((s) => String(s.ts_id) === String(sourceTsId));
    if (!row) return "";
    const name = formatTrainingSessionLabel(row, t);
    const day = sessionCalendarDayKey(row.ts_date || row.starts_at);
    return day ? `${name} · ${day}` : name;
  }, [importSessions, sourceTsId, t]);

  const ensureGeometry = useCallback(async (tableId) => {
    if (!tableId) return null;
    const key = String(tableId);
    if (geoCacheRef.current.has(key)) {
      return geoCacheRef.current.get(key);
    }
    const promise = getActiveTableGeometry(key).catch(() => null);
    geoCacheRef.current.set(key, promise);
    return promise;
  }, []);

  const handlePlay = useCallback(
    (videoUrl, title, messageId) => {
      const clip =
        displayedClips.find((c) => String(c.message_id) === String(messageId)) ||
        null;
      const payload = {
        ...(clip || {}),
        message_id: messageId,
        video_url: videoUrl,
        video_message_url: clip?.video_message_url || videoUrl,
        title:
          title ||
          (clip?.kind === "table_replay"
            ? t("liveTrainingReplayTitle", "Last 10 seconds")
            : clip?.is_kot
              ? t("kotVideo", "KoT video")
              : t("evaluationVideo", "Evaluation Video")),
        te_id: clip?.te_id,
        table_id: clip?.table_id,
        view_setup: clip?.view_setup,
      };
      if (!payload.video_url) return;
      setLastPlayedId(messageId != null ? String(messageId) : null);
      setPlayback(payload);
      onPlayClip?.(payload);
    },
    [displayedClips, onPlayClip, t]
  );

  const scrollClipIntoView = useCallback((messageId) => {
    if (messageId == null) return;
    const root = cardsRef.current;
    if (!root) return;
    const escaped =
      typeof CSS !== "undefined" && typeof CSS.escape === "function"
        ? CSS.escape(String(messageId))
        : String(messageId).replace(/"/g, "");
    const node = root.querySelector(`[data-session-clip-id="${escaped}"]`);
    if (!node) return;
    const reduce =
      typeof window !== "undefined" &&
      window.matchMedia?.("(prefers-reduced-motion: reduce)")?.matches;
    node.scrollIntoView({
      block: "nearest",
      inline: "nearest",
      behavior: reduce ? "auto" : "smooth",
    });
  }, []);

  const handleClosePlayback = useCallback(() => {
    const id =
      playback?.message_id != null ? String(playback.message_id) : lastPlayedId;
    setPlayback(null);
    if (!id) return;
    setLastPlayedId(id);
    setReturnPulseId(id);
    if (returnPulseTimerRef.current) {
      clearTimeout(returnPulseTimerRef.current);
    }
    returnPulseTimerRef.current = setTimeout(() => {
      setReturnPulseId((cur) => (cur === id ? null : cur));
    }, 1700);
    requestAnimationFrame(() => scrollClipIntoView(id));
  }, [playback, lastPlayedId, scrollClipIntoView]);

  useEffect(
    () => () => {
      if (returnPulseTimerRef.current) clearTimeout(returnPulseTimerRef.current);
    },
    []
  );

  const handleReencode = useCallback(
    async (msg) => {
      const id = String(msg?.message_id || "");
      if (!id) return;
      if (jobs[id]?.status === "running") return;
      if (!hasOrientVideoTransform(msg?.view_setup)) {
        window.alert(
          t(
            "bakeOrientsNoneOne",
            "This video has no rotation/flip to bake. Open the player, set orientation, then re-encode."
          )
        );
        return;
      }
      const confirmed = window.confirm(
        t(
          "confirmVideoReencodeReplace",
          "Re-encode this video with the saved rotation/flip and replace the file?"
        )
      );
      if (!confirmed) return;

      abortByIdRef.current.get(id)?.abort();
      const ac = new AbortController();
      abortByIdRef.current.set(id, ac);
      setBakingMessageId(id);
      updateJob(id, {
        kind: "reencode",
        status: "running",
        progress: 0,
        etaSeconds: null,
        error: null,
      });

      try {
        const job = await reencodeEvaluationVideo(
          id,
          {},
          {
            signal: ac.signal,
            onProgress: (jobStatus) => {
              updateJob(id, {
                kind: "reencode",
                status: "running",
                progress: Number(jobStatus?.progress) || 0,
                etaSeconds:
                  jobStatus?.eta_seconds != null
                    ? Number(jobStatus.eta_seconds)
                    : null,
                error: null,
              });
            },
          }
        );
        const data = job?.data || {};
        setClips((prev) =>
          prev.map((c) =>
            String(c.message_id) === id
              ? {
                  ...c,
                  video_url: data.video_message_url || c.video_url,
                  view_setup: data.view_setup ?? c.view_setup,
                }
              : c
          )
        );
        updateJob(id, {
          kind: "reencode",
          status: "done",
          progress: 1,
          etaSeconds: 0,
          error: null,
        });
        clearJobSoon(id);
      } catch (err) {
        if (err?.name === "AbortError") return;
        updateJob(id, {
          kind: "reencode",
          status: "error",
          progress: 0,
          etaSeconds: null,
          error: err?.message || t("bakeOrientsFailed", "Re-encode failed"),
        });
        clearJobSoon(id, 5000);
      } finally {
        setBakingMessageId((cur) => (String(cur) === id ? null : cur));
        abortByIdRef.current.delete(id);
      }
    },
    [jobs, t, updateJob, clearJobSoon]
  );

  const runPoseForClip = useCallback(
    async (msg, { setSingleDetecting = true } = {}) => {
      const id = String(msg?.message_id || "");
      const teId = msg?.te_id;
      if (!id || !teId) return { ok: false, skipped: true };
      if (jobs[id]?.status === "running") return { ok: false, skipped: true };

      abortByIdRef.current.get(id)?.abort();
      const ac = new AbortController();
      abortByIdRef.current.set(id, ac);
      if (setSingleDetecting) setDetectingMessageId(id);
      updateJob(id, {
        kind: "pose",
        status: "running",
        progress: 0,
        etaSeconds: null,
        error: null,
      });

      try {
        const settings = loadVideoAnalysisSettings();
        const geo = await ensureGeometry(msg.table_id);
        const mode = settings.poseDetectionPlayer || "A";
        const calibrated = roisForPoseDetectionPlayer(mode, {
          left: geo?.roi_left || null,
          right: geo?.roi_right || null,
        });
        // Incomplete ROI calib → omit ROIs; pipeline picks two closest
        // players (leftiest = A, rightiest = B).
        const roisComplete =
          (mode === "A" && calibrated.roiLeft) ||
          (mode === "B" && calibrated.roiRight) ||
          (mode === "Both" && calibrated.roiLeft && calibrated.roiRight);
        const roiLeft = roisComplete ? calibrated.roiLeft : null;
        const roiRight = roisComplete ? calibrated.roiRight : null;

        const absUrl =
          toAbsoluteMediaUrl(msg.video_message_url, ip_back) ||
          msg.video_message_url;
        const result = await fetchOrEnqueuePoseResult(teId, {
          poseFps: settings.poseFps,
          players: mode,
          roiLeft,
          roiRight,
          videoUrl: absUrl,
          sequenceOrder: msg.sequence_number ?? msg.sequence_order ?? null,
          viewSetup: msg.view_setup || null,
          signal: ac.signal,
          onProgress: ({ progress, etaSeconds }) => {
            updateJob(id, {
              kind: "pose",
              status: "running",
              progress: Number(progress) || 0,
              etaSeconds:
                etaSeconds != null && Number.isFinite(Number(etaSeconds))
                  ? Number(etaSeconds)
                  : null,
              error: null,
            });
          },
        });
        updateJob(id, {
          kind: "pose",
          status: "done",
          progress: 1,
          etaSeconds: 0,
          error: null,
        });
        onPoseComplete?.({
          clip: clips.find((c) => String(c.message_id) === id) || msg,
          result,
        });
        clearJobSoon(id);
        return { ok: true, id, result };
      } catch (err) {
        if (err?.name === "AbortError") return { ok: false, aborted: true };
        updateJob(id, {
          kind: "pose",
          status: "error",
          progress: 0,
          etaSeconds: null,
          error:
            err?.message ||
            t("sessionMediaPoseFailed", "Stroke detection failed"),
        });
        clearJobSoon(id, 6000);
        return { ok: false, id, error: err?.message };
      } finally {
        if (setSingleDetecting) {
          setDetectingMessageId((cur) => (String(cur) === id ? null : cur));
        }
        abortByIdRef.current.delete(id);
      }
    },
    [jobs, t, updateJob, clearJobSoon, ensureGeometry, onPoseComplete, clips]
  );

  const handlePose = useCallback(
    async (msg) => {
      await runPoseForClip(msg, { setSingleDetecting: true });
    },
    [runPoseForClip]
  );

  const handleBatchPoseFiltered = useCallback(async () => {
    if (batchDetectBusy || bakingMessageId != null) return;
    const targets = filtered
      .map((clip) => clipToMsg(clip))
      .filter((m) => m.message_id && m.te_id);
    if (!targets.length) {
      window.alert(
        t(
          "sessionMediaBatchPoseEmpty",
          "No videos in this filter to run stroke detection on."
        )
      );
      return;
    }

    const settings = loadVideoAnalysisSettings();
    const filterName =
      filter === "evaluation"
        ? t("sessionMediaFilterEval", "Evaluation")
        : filter === "kot"
          ? t("sessionMediaFilterKot", "KoT")
          : filter === "table_replay"
            ? t("sessionMediaFilterReplay", "Replay")
            : t("sessionMediaFilterAll", "All");
    const confirmed = window.confirm(
      t(
        "sessionMediaBatchPoseConfirm",
        "Run FH stroke detection on {{count}} {{filter}} video(s) at {{fps}} fps (player {{player}})?",
        {
          count: targets.length,
          filter: filterName,
          fps: settings.poseFps,
          player: settings.poseDetectionPlayer || "A",
        }
      )
    );
    if (!confirmed) return;

    setBatchDetectBusy(true);
    try {
      // Enqueue/poll all filtered clips; GCP pose worker parallelizes up to ×6.
      const settled = await Promise.allSettled(
        targets.map((msg) =>
          runPoseForClip(msg, { setSingleDetecting: false })
        )
      );
      const ok = settled.filter(
        (r) => r.status === "fulfilled" && r.value?.ok
      ).length;
      const failed = settled.length - ok;
      if (failed > 0) {
        window.alert(
          t(
            "sessionMediaBatchPosePartial",
            "Stroke detection finished: {{ok}} ok, {{fail}} failed/skipped.",
            { ok, fail: failed }
          )
        );
      }
    } finally {
      setBatchDetectBusy(false);
    }
  }, [
    batchDetectBusy,
    bakingMessageId,
    filtered,
    filter,
    runPoseForClip,
    t,
  ]);

  const anyBakeBusy = bakingMessageId != null;
  const anyDetectBusy = detectingMessageId != null || batchDetectBusy;

  return (
    <>
    <Dialog
      open={Boolean(open)}
      onClose={(event, reason) => {
        if (playback) return;
        onClose?.(event, reason);
      }}
      maxWidth="sm"
      fullWidth
      disableEnforceFocus={Boolean(playback)}
      className="session-media-files-dialog"
      aria-labelledby="session-media-files-title"
      sx={{ zIndex: 13000 }}
      PaperProps={{
        className: "session-media-files-dialog__paper",
      }}
    >
      <DialogTitle
        id="session-media-files-title"
        className="session-media-files-dialog__title"
      >
        <div className="session-media-files-dialog__title-row">
          <div className="session-media-files-dialog__title-text">
            <span className="session-media-files-dialog__title-main">
              {t("sessionMediaFiles", "Session media")}
            </span>
            <span className="session-media-files-dialog__subtitle">
              {t(
                "sessionMediaFilesSubtitle",
                "Evaluation and King of the Table videos for this session"
              )}
            </span>
          </div>
          <IconButton
            type="button"
            onClick={onClose}
            aria-label={t("close", "Close")}
            className="session-media-files-dialog__close"
            size="small"
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </div>
      </DialogTitle>
      <DialogContent>
        <div className="session-media-files-dialog__import">
          <label
            className="session-media-files-dialog__import-label"
            htmlFor="session-media-import-source"
          >
            {t(
              "sessionMediaImportFromSession",
              "Match videos from another session"
            )}
          </label>
          <select
            id="session-media-import-source"
            className="session-media-files-dialog__import-select"
            value={sourceTsId}
            disabled={importSessionsLoading}
            onChange={(e) => setSourceTsId(e.target.value)}
          >
            <option value="">
              {t("sessionMediaThisSession", "This session")}
            </option>
            {importSessions.map((row) => {
              const name = formatTrainingSessionLabel(row, t);
              const day = sessionCalendarDayKey(row.ts_date || row.starts_at);
              return (
                <option key={row.ts_id} value={row.ts_id}>
                  {day ? `${name} · ${day}` : name}
                </option>
              );
            })}
          </select>
          <p className="session-media-files-dialog__import-hint">
            {viewingImport
              ? t(
                  "sessionMediaImportHintActive",
                  "Clips from {{session}}. Move each one onto an exercise in this session.",
                  { session: sourceSessionLabel }
                )
              : t(
                  "sessionMediaImportHint",
                  "Use this when clips were captured in a phone session and this session was created afterwards."
                )}
          </p>
          {importSessionsLoading ? (
            <p className="session-media-files-dialog__import-status">
              {t("loading", "Loading…")}
            </p>
          ) : null}
          {!importSessionsLoading && importSessions.length === 0 ? (
            <p className="session-media-files-dialog__import-status">
              {t(
                "sessionMediaImportNone",
                "No other sessions found on this date."
              )}
            </p>
          ) : null}
        </div>
        <div
          className="session-media-files-dialog__filters"
          role="tablist"
          aria-label={t("sessionMediaFilter", "Filter media")}
        >
          {FILTERS.map((key) => {
            const selected = filter === key;
            return (
              <button
                key={key}
                type="button"
                role="tab"
                aria-selected={selected}
                className={
                  selected
                    ? "session-media-files-dialog__chip is-selected"
                    : "session-media-files-dialog__chip"
                }
                onClick={() => setFilter(key)}
              >
                {filterLabels[key]}
                <span className="session-media-files-dialog__chip-count">
                  {filterCounts[key]}
                </span>
              </button>
            );
          })}
        </div>

        <div className="session-media-files-dialog__batch-actions">
          <button
            type="button"
            className="session-media-files-dialog__batch-btn"
            disabled={
              viewingImport ||
              loading ||
              batchDetectBusy ||
              anyBakeBusy ||
              filtered.length === 0
            }
            onClick={handleBatchPoseFiltered}
          >
            {batchDetectBusy ? (
              <>
                <CircularProgress size={14} style={{ color: "inherit" }} />
                {t(
                  "sessionMediaBatchPoseWorking",
                  "Detecting FH strokes…"
                )}
              </>
            ) : (
              t(
                "sessionMediaBatchPose",
                "Detect FH strokes on {{count}} {{filter}} video(s)",
                {
                  count: filtered.length,
                  filter:
                    filter === "evaluation"
                      ? t("sessionMediaFilterEval", "Evaluation")
                      : filter === "kot"
                        ? t("sessionMediaFilterKot", "KoT")
                        : filter === "table_replay"
                          ? t("sessionMediaFilterReplay", "Replay")
                          : t("sessionMediaFilterAll", "All"),
                }
              )
            )}
          </button>
          <p className="session-media-files-dialog__batch-hint">
            {t(
              "sessionMediaBatchPoseHint",
              "Uses video analysis settings (FPS + player). Respects the filter above."
            )}
          </p>
        </div>

        {runningJobs.length > 0 ? (
          <div
            className="session-media-files-dialog__busy-banner"
            role="status"
            aria-live="polite"
          >
            <CircularProgress size={14} style={{ color: "#0A84FF" }} />
            <span>
              {t(
                "sessionMediaProcessingN",
                "Processing {{count}} video(s)",
                { count: runningJobs.length }
              )}
              {longestEta != null
                ? ` · ${t("sessionMediaEtaShort", "{{eta}} left", {
                    eta: formatReencodeEta(longestEta),
                  })}`
                : ""}
            </span>
          </div>
        ) : null}

        {moveStatus ? (
          <div
            className={
              moveStatus.success
                ? "session-media-files-dialog__status session-media-files-dialog__status--ok"
                : "session-media-files-dialog__status session-media-files-dialog__status--error"
            }
            role="status"
          >
            {moveStatus.message}
          </div>
        ) : null}

        {(viewingImport ? sourceLoading : loading) ? (
          <div className="session-media-files-dialog__state">
            <CircularProgress size={28} style={{ color: "#0A84FF" }} />
            <p>{t("loading", "Loading…")}</p>
          </div>
        ) : (viewingImport ? sourceError : error) ? (
          <div className="session-media-files-dialog__state session-media-files-dialog__state--error">
            <p>{viewingImport ? sourceError : error}</p>
          </div>
        ) : filtered.length === 0 ? (
          <div className="session-media-files-dialog__state">
            <p>
              {viewingImport
                ? t(
                    "sessionMediaImportEmpty",
                    "No evaluation or KoT videos in that session."
                  )
                : t(
                    "sessionMediaEmpty",
                    "No evaluation or KoT videos in this session yet."
                  )}
            </p>
          </div>
        ) : (
          <div
            className="session-media-files-dialog__cards player-exercise-details__eval-video-list"
            ref={cardsRef}
          >
            {returnPulseId ? (
              <p className="session-media-files-dialog__return" role="status">
                {t("sessionMediaReturned", "Back to the video you were watching")}
              </p>
            ) : null}
            {filtered.map((clip, index) => {
              const msg = clipToMsg(clip);
              const meta = buildSessionClipMeta(
                clip,
                exercises,
                localeCode,
                t
              );
              const absUrl =
                toAbsoluteMediaUrl(clip.video_url, ip_back) || clip.video_url;
              const job = jobs[String(clip.message_id)] || null;
              return (
                <EvaluationVideoMediaCard
                  key={clip.message_id || `${clip.video_url}-${index}`}
                  msg={msg}
                  meta={meta}
                  formattedDate={formatEvaluationVideoCardDate(msg, localeCode)}
                  videoUrl={absUrl}
                  playLabel={t("watchEvaluationVideo", "Watch evaluation video")}
                  onPlay={handlePlay}
                  density="compact"
                  isActivePlayback={
                    lastPlayedId != null &&
                    String(clip.message_id) === String(lastPlayedId)
                  }
                  isReturnPulse={
                    returnPulseId != null &&
                    String(clip.message_id) === String(returnPulseId)
                  }
                  canMoveVideo
                  onMoveVideo={setMoveVideoTarget}
                  canBakeOrient
                  bakeOrientBusy={anyBakeBusy}
                  bakingMessageId={bakingMessageId}
                  onBakeOrient={handleReencode}
                  canDetectStrokes
                  detectStrokesBusy={anyDetectBusy}
                  detectingMessageId={detectingMessageId}
                  onDetectStrokes={handlePose}
                  jobStatus={job}
                  canShareReel
                  onShareReel={(targetMsg, targetUrl) =>
                    setReelShareTarget({
                      videoUrl: targetUrl,
                      kind: isMaxRallyEvaluationVideo(targetMsg)
                        ? "max_rally"
                        : isStrokeSlowMoEvaluationVideo(targetMsg)
                          ? "stroke_slowmo"
                          : "max_rally",
                    })
                  }
                  t={t}
                />
              );
            })}
          </div>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>{t("close", "Close")}</Button>
      </DialogActions>
    </Dialog>
    <MoveEvaluationVideoDialog
      open={Boolean(moveVideoTarget)}
      onClose={() => setMoveVideoTarget(null)}
      message={moveVideoTarget}
      tsId={tsId}
      currentTeId={moveVideoTarget?.te_id || null}
      onMoved={handleMoveVideoSuccess}
      onDeleted={handleDeleteVideoSuccess}
      zIndex={15000}
    />
    <ClipShareSheet
      open={Boolean(reelShareTarget)}
      onClose={() => setReelShareTarget(null)}
      videoUrl={reelShareTarget?.videoUrl || null}
      kind={reelShareTarget?.kind || "max_rally"}
      t={t}
    />
    <ReviewVideoFullscreenPlayer
      url={playback?.video_url || null}
      title={playback?.title}
      onClose={handleClosePlayback}
      canTransformVideo
      messageId={playback?.message_id || null}
      teId={playback?.te_id || null}
      tableId={playback?.table_id || null}
      initialViewSetup={playback?.view_setup || null}
      sequenceOrder={
        playback?.sequence_number ?? playback?.sequence_order ?? null
      }
      enablePose
      isKotMatch={Boolean(playback?.is_kot || playback?.kind === "kot")}
      kotMatchId={playback?.kot_match_id || null}
      isEvaluationVideo={playback?.kind !== "table_replay"}
      isShareableReel={
        Boolean(playback) &&
        (isMaxRallyEvaluationVideo(playback) ||
          isStrokeSlowMoEvaluationVideo(playback))
      }
      shareableReelKind={
        playback && isStrokeSlowMoEvaluationVideo(playback)
          ? "stroke_slowmo"
          : "max_rally"
      }
    />
    </>
  );
}

export default SessionMediaFilesDialog;
